/* eslint-disable @typescript-eslint/return-await */
import React, { useState } from 'react'
import Dropdown from '../../../components/Dropdown'
import GenericModal from '../../../components/GenericModal'
import CreateEditDivision from '../CreateDivision'
import { useAppDispatch } from '../../../store'
import { getDivisionList } from '../../../store/slices/reportHierarchySlice/apis'
import { divisionList } from '../../../store/slices/reportHierarchySlice/selectors'
import { TableModalAction } from '../../../components/CustomTable/types'
import CsvMappingUpload from '../../../components/CsvMappingUpload'
import { notification } from '../../../components/Notifications'
import { useMutation } from '@tanstack/react-query'
import { resetDivisionsByDivisionId } from '../../../APIManager/DivisionManager'
import { type ApiError } from '../../../models/Common'
import { ButtonType } from '../../../components/GenericButton/styles'
import { grey } from '../../../colors'
interface ReportHierarchyTopbarActionsProps {
  categoryName?: string
  allowedActions?: string[]
  breadcrumbId?: string
  params?: Record<string, any>
  setUploadTaskId: (id: string | null) => void
  isTaskRunning: boolean
  onTaskBlock: () => void
}

const ReportHierarchyTopbarActions: React.FC<ReportHierarchyTopbarActionsProps> = ({ categoryName, allowedActions, breadcrumbId, params, setUploadTaskId, isTaskRunning, onTaskBlock }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [showResetConfirmationModal, setShowResetConfirmationModal] = useState(false)
  const dispatch = useAppDispatch()

  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const uploadApi = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsUploadModal(true)
  }

  const addManually = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsCreateModal(true)
  }

  const menuItems = [
    { text: `Upload ${categoryName ?? 'Items'} (.csv)`, action: uploadApi },
    { text: 'Add Manually', action: addManually }
  ]

  const resetTooltip = `Deletes all ${categoryName ?? 'items'} (children) that do not have a survey in published, live or closed state. This action cannot be undone.`
  const resetModalText = `Deletes all ${categoryName ?? 'items'} (children) that do not have a survey in published, live or closed state. This action cannot be undone.`
  const { mutate: resetDivisions, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return await resetDivisionsByDivisionId(breadcrumbId ?? '')
    },
    onSuccess: (response) => {
      notification(response?.data?.message ?? 'Reset successful', { variant: 'success' })
      if (breadcrumbId) {
        void dispatch(getDivisionList({ urlId: breadcrumbId, params }))
      }
      setShowResetConfirmationModal(false)
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset divisions.', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const resetMenuItems = [
    {
      text: `Reset ${categoryName ?? 'Items'}`,
      action: () => { setShowResetConfirmationModal(true) },
      disabled: isTaskRunning,
      tooltipTitle: resetTooltip
    }
  ]

  return (
    <>

      {allowedActions?.includes(TableModalAction.CREATE) && <Dropdown menuItems={menuItems} label={`Add ${categoryName ?? 'Items'}`} />}
      <GenericModal
        isOpen={isCreateModal}
        title={`Create ${categoryName ?? 'Item'}`}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateEditDivision handleCloseModal={() => { setIsCreateModal(false) }} />
      </GenericModal>

      <CsvMappingUpload
        csvHeaderEndpoint="/api/divisions/csv_headers"
        isOpen={isUploadModal}
        modalTitle={categoryName ?? 'Items'}
        onClose={closeUploadModal}
        uploadCsvEndpoint={`/api/divisions/?category_name=${categoryName ?? 'Department'}`}
        uploadCsvEndpointApiParams={breadcrumbId ? { parent_id: breadcrumbId } : undefined}
        hasCreatePermission={allowedActions?.includes(TableModalAction.CREATE)}
        returnTaskId={(id) => { if (id) setUploadTaskId(id) }}
        onSuccess={() => {
          notification(`${categoryName ?? 'Items'} successfully uploaded`, { variant: 'success' })
          if (breadcrumbId) {
            void dispatch(getDivisionList({ urlId: breadcrumbId, params }))
          }
        }} />

      {allowedActions?.includes(TableModalAction.DELETE) && (
        <>
          <GenericModal
            isOpen={showResetConfirmationModal}
            onClose={() => { setShowResetConfirmationModal(false) }}
            title={`Reset ${categoryName ?? 'Items'}`}
            maxWidth='sm'
            onConfirm={async () => { resetDivisions() }}
            confirmButtonLoading={isResetting}
          >
            {resetModalText}
          </GenericModal>
        </>
      )}
    </>
  )
}

export default ReportHierarchyTopbarActions
